import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { UtensilsCrossed, ShoppingBag, CalendarCheck, Building2, Briefcase, Check, ArrowLeft, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { t as tr } from '../../i18n'

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurant / Cafe', desc: 'QR menu, table ordering, kitchen display', icon: UtensilsCrossed },
  { value: 'ecommerce', label: 'Online Store', desc: 'Sell products with a cart and checkout', icon: ShoppingBag },
  { value: 'appointments', label: 'Appointments', desc: 'Clinics, salons, or any service you book by the slot', icon: CalendarCheck },
  { value: 'hotel', label: 'Hotel', desc: 'Room bookings by date and time slot', icon: Building2 },
  { value: 'office', label: 'Office / Coworking', desc: 'Desk or meeting-room bookings', icon: Briefcase },
];

export default function GetStartedPage() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    businessType: '',
    storeName: '',
    subdomain: '',
    fullName: '',
    email: '',
    username: '',
    password: '',
  });

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Web address (subdomain): follows the store name until the owner edits it,
  // and is checked live so a name already in use is caught before signup.
  const [subdomainEdited, setSubdomainEdited] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ state: 'idle' });
  const toSlug = value => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  const setStoreName = value => setForm(f => ({ ...f, storeName: value, subdomain: subdomainEdited ? f.subdomain : toSlug(value) }));

  useEffect(() => {
    const name = form.subdomain;
    if (!name) { setSlugStatus({ state: 'idle' }); return; }
    setSlugStatus(s => ({ ...s, state: 'checking' }));
    const timer = setTimeout(() => {
      authAPI.checkSubdomain(name)
        .then(({ data }) => setSlugStatus({ state: data.available ? 'ok' : 'bad', ...data }))
        .catch(() => setSlugStatus({ state: 'idle' }));
    }, 350);
    return () => clearTimeout(timer);
  }, [form.subdomain]);

  const next = () => {
    if (step === 1 && !form.businessType) { toast.error(tr("Pick what kind of business you run")); return; }
    if (step === 2 && !form.storeName) { toast.error(tr("Give your store a name")); return; }
    if (step === 2 && slugStatus.state !== 'ok') { toast.error(tr(slugStatus.error || 'Choose a web address for your store')); return; }
    setStep(s => s + 1);
  };
  const back = () => setStep(s => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.username || !form.password) {
      toast.error(tr("Fill in your name, username, and password"));
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await authAPI.register(form);
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      toast.success(tr("{name} is ready!", { name: data.store.name }));
      window.location.href = '/admin';
    } catch (error) {
      toast.error(tr(error.response?.data?.error || 'Could not create your account'));
      // Someone took the name meanwhile: go back so the owner can change it.
      if (['SUBDOMAIN_TAKEN', 'SUBDOMAIN_INVALID'].includes(error.response?.data?.code)) {
        setSlugStatus({ state: 'bad', error: error.response.data.error, suggestion: error.response.data.suggestion });
        setStep(2);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Link to="/" className="flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> {tr("Back to home")}
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3].map(n => (
              <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? 'bg-blue-600' : 'bg-slate-200'}`} />
            ))}
          </div>

          {step === 1 && (
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{tr("What are you setting up?")}</h1>
              <p className="text-slate-500 mb-6">{tr("Pick the kind of business you're building — you'll get the right tools for it.")}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {BUSINESS_TYPES.map(t => (
                  <button key={t.value} type="button" onClick={() => update('businessType', t.value)}
                    className={`text-start p-4 rounded-xl border-2 transition-all ${form.businessType === t.value ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <t.icon className={`w-6 h-6 mb-2 ${form.businessType === t.value ? 'text-blue-600' : 'text-slate-400'}`} />
                    <div className="font-semibold text-slate-900">{tr(t.label)}</div>
                    <div className="text-sm text-slate-500">{tr(t.desc)}</div>
                  </button>
                ))}
              </div>
              <button onClick={next} className="mt-8 w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-medium py-3 rounded-xl hover:bg-blue-700">
                {tr("Continue")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{tr("Name your store")}</h1>
              <p className="text-slate-500 mb-6">{tr("This becomes your subdomain and shows to customers.")}</p>
              <input autoFocus required placeholder={tr("e.g., Bella Vista Cafe")} value={form.storeName}
                onChange={e => setStoreName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <label htmlFor="subdomain" className="block text-sm font-medium text-slate-700 mt-5 mb-1.5">{tr("Your web address")}</label>
              <div dir="ltr" className={`flex items-stretch rounded-xl border overflow-hidden focus-within:ring-2 ${slugStatus.state === 'bad' ? 'border-red-300 focus-within:ring-red-200' : 'border-slate-200 focus-within:ring-blue-500'}`}>
                <span className="px-3 grid place-items-center bg-slate-50 text-slate-500 text-sm border-e border-slate-200">https://</span>
                <input id="subdomain" value={form.subdomain} spellCheck={false} autoCapitalize="none" placeholder="my-store"
                  onChange={e => { setSubdomainEdited(true); update('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-{2,}/g, '-').replace(/^-+/, '').slice(0, 40)); }}
                  className="flex-1 min-w-0 px-3 py-3 outline-none" />
                <span className="px-3 grid place-items-center bg-slate-50 text-slate-500 text-sm border-s border-slate-200 whitespace-nowrap">.{slugStatus.domain || 'yourdomain.com'}</span>
              </div>
              <p className={`text-sm mt-1.5 ${slugStatus.state === 'bad' ? 'text-red-600' : slugStatus.state === 'ok' ? 'text-emerald-700' : 'text-slate-500'}`} aria-live="polite">
                {slugStatus.state === 'checking' && tr('Checking...')}
                {slugStatus.state === 'ok' && tr('Available - this will be your website address.')}
                {slugStatus.state === 'bad' && tr(slugStatus.error || 'This name is not available')}
                {slugStatus.state === 'idle' && tr('English letters, numbers and dashes only.')}
              </p>
              {slugStatus.state === 'bad' && slugStatus.suggestion && (
                <button type="button" onClick={() => { setSubdomainEdited(true); update('subdomain', slugStatus.suggestion); }} className="text-sm font-medium text-blue-700 hover:underline mt-1">
                  {tr('Use "{name}" instead', { name: slugStatus.suggestion })}
                </button>
              )}
              <input placeholder={tr("Contact email (optional)")} type="email" value={form.email}
                onChange={e => update('email', e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl mt-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <div className="flex gap-3 mt-8">
                <button onClick={back} className="flex-1 flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-medium py-3 rounded-xl hover:bg-slate-50">
                  <ArrowLeft className="w-4 h-4" /> {tr("Back")}
                </button>
                <button onClick={next} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white font-medium py-3 rounded-xl hover:bg-blue-700">
                  {tr("Continue")} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <form onSubmit={submit}>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{tr("Create your account")}</h1>
              <p className="text-slate-500 mb-6">{tr("This is how you'll log in and manage")} {form.storeName || tr("your store")}.</p>
              <div className="space-y-3">
                <input required placeholder={tr("Full name")} value={form.fullName}
                  onChange={e => update('fullName', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl" />
                <input required placeholder={tr("Username")} value={form.username}
                  onChange={e => update('username', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl" />
                <input required type="password" placeholder={tr("Password (min. 6 characters)")} value={form.password}
                  onChange={e => update('password', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl" />
              </div>
              <div className="flex gap-3 mt-8">
                <button type="button" onClick={back} className="flex-1 flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-medium py-3 rounded-xl hover:bg-slate-50">
                  <ArrowLeft className="w-4 h-4" /> {tr("Back")}
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white font-medium py-3 rounded-xl hover:bg-green-700 disabled:opacity-50">
                  <Check className="w-4 h-4" /> {submitting ? tr("Creating...") : tr("Create my store")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
